import {ref, computed, onMounted, onUnmounted} from 'vue';

// ============================================================================
// 响应式断点体系 —— PC 宽屏适配的统一入口
//
// 背景: 本项目移动端用 postcss-px-to-viewport 把 px 全部转成 vw(相对窗口宽),
// 在 PC 上会等比放大到巨大。桌面端布局改为在 @media(min-width) 里写真实 px 覆盖
// (postcss mediaQuery:false 保证 @media 内的 px 不被转 vw), 并用本 composable
// 统一提供「是否桌面 / 内容区实宽 / 列数」给需要按窗口宽计算尺寸的 JS 逻辑,
// 替换散落各处的裸 window.innerWidth 读取。
// ============================================================================

// 桌面断点(与各组件 @media 里的 min-width 保持一致): 窗口 >= 此宽度才用 PC 版
export const DESKTOP_BP = 1024;
// 手机壳目标宽度: 窗口在 (PHONE_FRAME_W, DESKTOP_BP) 之间时, 移动版缩放居中成这个宽度的手机
export const PHONE_FRAME_W = 390;
// 桌面内容区居中定宽最大宽度(与 desktop.css 的 --pc-content-w 保持一致)
export const DESKTOP_CONTENT_WIDTH = 1200;
// 内容区左右内边距(桌面),用于把「内容可用宽」算准
export const DESKTOP_CONTENT_PADDING = 16;

const mql = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia(`(min-width:${DESKTOP_BP}px)`)
  : null;

// 是否处于 iframe 内(手机壳里加载的那份 app)。手机壳里恒按真实 390 视口跑移动版, 不再套壳。
export const IS_FRAMED = (() => {
  try { return window.self !== window.top; } catch (e) { return true; }
})();

function _computePhoneFrame(w) {
  // 手机壳档: 非 iframe 内、且窗口宽度介于 (PHONE_FRAME_W, DESKTOP_BP)
  return !IS_FRAMED && w > PHONE_FRAME_W && w < DESKTOP_BP;
}

// 全局共享的响应式态(模块级单例, 避免每个组件各自监听)
const _isDesktop = ref(mql ? mql.matches : false);
const _winW = ref(typeof window !== 'undefined' ? window.innerWidth : DESKTOP_BP);
const _phoneFrame = ref(typeof window !== 'undefined' ? _computePhoneFrame(window.innerWidth) : false);

function _sync() {
  _isDesktop.value = mql ? mql.matches : window.innerWidth >= DESKTOP_BP;
  _winW.value = window.innerWidth;
  _phoneFrame.value = _computePhoneFrame(window.innerWidth);
}

let _listening = false;
function _startListen() {
  if (_listening || typeof window === 'undefined') return;
  _listening = true;
  window.addEventListener('resize', _sync);
  // matchMedia change 更精准(断点跨越时触发)
  if (mql) {
    if (mql.addEventListener) mql.addEventListener('change', _sync);
    else if (mql.addListener) mql.addListener(_sync); // 老浏览器兜底
  }
}

// 立即开始监听(模块被引入即生效, 保证任意组件读到的都是最新态)
_startListen();

/**
 * 统一的响应式信息。任意组件可直接解构使用。
 * - isDesktop: 是否桌面宽屏(>=1024)
 * - contentWidth: 桌面下=居中容器可用宽(min(窗口,1200)-padding); 移动下=窗口宽
 *   —— 需要按「内容区宽」算列宽的组件请读它, 不要再读 window.innerWidth
 */
export function useResponsive() {
  const isDesktop = _isDesktop;

  const contentWidth = computed(() => {
    if (_isDesktop.value) {
      const w = Math.min(_winW.value, DESKTOP_CONTENT_WIDTH);
      return w - DESKTOP_CONTENT_PADDING * 2;
    }
    return _winW.value;
  });

  return {
    isDesktop,
    winWidth: _winW,
    contentWidth,
    phoneFrameMode: _phoneFrame,
  };
}

/**
 * 非组件环境(纯 JS 工具)取内容区可用宽的即时值。
 */
export function getContentWidth() {
  const desktop = mql ? mql.matches : window.innerWidth >= DESKTOP_BP;
  if (desktop) {
    return Math.min(window.innerWidth, DESKTOP_CONTENT_WIDTH) - DESKTOP_CONTENT_PADDING * 2;
  }
  return document.documentElement.clientWidth || window.innerWidth;
}
